/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
  linkedSignal,
  output,
  OutputEmitterRef,
  WritableSignal,
} from '@angular/core';
import { cva, VariantProps } from 'class-variance-authority';
import { uniqueId } from 'lodash-es';
import { IconComponent } from '../icon/icon.component';

export const summaryVariants = cva(
  'text-text hover:bg-background-100 focus-visible:outline-focus flex cursor-pointer list-none items-center gap-1.5 rounded font-medium select-none focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-solid',
  {
    variants: {
      size: {
        small: 'text-sm',
        medium: 'text-base',
        large: 'text-xl',
      },
      padding: {
        medium: 'px-2 py-1.5',
        none: '',
      },
    },
    defaultVariants: {
      size: 'medium',
      padding: 'medium',
    },
  },
);
export type SummaryVariants = VariantProps<typeof summaryVariants>;

export const contentVariants = cva('pt-2', {
  variants: {
    padding: {
      medium: 'px-4',
      indented: 'pl-14 pb-4',
      none: 'px-0',
    },
  },
  defaultVariants: {
    padding: 'medium',
  },
});
export type ContentVariants = VariantProps<typeof contentVariants>;

export const collapsibleIconVariants = cva('shrink-0', {
  variants: {
    position: {
      left: '',
      right: 'order-last ml-auto pr-2',
    },
  },
  defaultVariants: {
    position: 'left',
  },
});

export type CollapsibleSectionHeadingLevel = 2 | 3 | 4 | 5 | 6;

export type CollapsibleSectionIconPosition = 'left' | 'right';

@Component({
  selector: 'ods-collapsible-section',
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `<details class="group" [open]="isOpen()" (toggle)="onToggle($event)" data-test-id="collapsible-section-details">
    <summary
      [class]="summaryVariants({ size: labelSize(), padding: labelPadding() })"
      [attr.aria-expanded]="isOpen()"
      data-test-class="collapsible-section-summary"
    >
      <ods-icon
        name="chevron-right"
        [config]="{ size: 'medium', fill: 'primary', animation: isOpen() ? 'rotate-90' : 'rotate-0' }"
        [class]="collapsibleIconVariants({ position: iconPosition() })"
        data-test-id="collapsible-section-icon"
      />
      @if (label()) {
        <span
          [id]="labelId"
          [attr.role]="headingLevel() ? 'heading' : null"
          [attr.aria-level]="headingLevel()"
          data-test-id="collapsible-section-label"
          >{{ label() }}</span
        >
      }
      <ng-content select="[collapsible-section-header]" />
    </summary>
    <section
      [attr.aria-labelledby]="label() ? labelId : null"
      [class]="contentVariants({ padding: contentPadding() })"
      data-test-id="collapsible-section-content"
    >
      <ng-content />
    </section>
  </details>`,
})
export class CollapsibleSectionComponent {
  public readonly label: InputSignal<string | undefined> = input<string>();
  public readonly headingLevel: InputSignal<CollapsibleSectionHeadingLevel | undefined> = input<CollapsibleSectionHeadingLevel>();
  public readonly labelSize: InputSignal<SummaryVariants['size']> = input<SummaryVariants['size']>();
  public readonly labelPadding: InputSignal<SummaryVariants['padding']> = input<SummaryVariants['padding']>();
  public readonly expanded: InputSignal<boolean> = input<boolean>(false);
  public readonly contentPadding: InputSignal<ContentVariants['padding']> = input<ContentVariants['padding']>();
  public readonly iconPosition: InputSignal<CollapsibleSectionIconPosition> = input<CollapsibleSectionIconPosition>('left');

  public readonly expandedChange: OutputEmitterRef<boolean> = output<boolean>();

  public readonly isOpen: WritableSignal<boolean> = linkedSignal<boolean>(() => this.expanded());
  public readonly labelId: string = uniqueId('ods-collapsible-section-label-');

  public readonly summaryVariants = summaryVariants;
  public readonly contentVariants = contentVariants;
  public readonly collapsibleIconVariants = collapsibleIconVariants;

  public onToggle(event: Event): void {
    const open: boolean = (event.target as HTMLDetailsElement).open;
    if (open === this.isOpen()) {
      return;
    }
    this.isOpen.set(open);
    this.expandedChange.emit(open);
  }
}
